import { useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { PageDetail } from 'shared';
import { api } from '../api';
import { LabeledField } from './LabeledField';
import { Note } from './Note';
import { DISPLAY, tokens } from '../theme';

/**
 * Thêm trang bằng URL.
 *
 * Thêm xong là đi thẳng sang màn chi tiết của trang vừa thêm: server đã quét luôn
 * trong cùng lượt gọi, nên có kết quả để xem ngay — quay về danh sách rồi bắt người
 * dùng tự tìm dòng mới là thêm một bước vô ích.
 */
export function AddPageDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');

  const add = useMutation({
    mutationFn: () => api.addPage({ url: url.trim(), name: name.trim() || undefined }),
    onSuccess: (page: PageDetail) => {
      void qc.invalidateQueries({ queryKey: ['registry'] });
      qc.setQueryData(['page', page.slug], page);
      close();
      navigate(`/pages/${page.slug}`);
    },
  });

  function close() {
    setUrl('');
    setName('');
    add.reset();
    onClose();
  }

  const canSubmit = url.trim().length > 0 && !add.isPending;

  return (
    <Dialog
      open={open}
      onClose={add.isPending ? undefined : close}
      fullWidth
      maxWidth="sm"
      slotProps={{ paper: { sx: { borderRadius: '14px' } } }}
    >
      <DialogTitle sx={{ fontFamily: DISPLAY, fontSize: 19, fontWeight: 600, color: tokens.ink }}>
        Thêm trang cần theo dõi
      </DialogTitle>

      <DialogContent>
        <Stack
          component="form"
          spacing={2}
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) add.mutate();
          }}
        >
          <LabeledField
            label="Địa chỉ trang"
            placeholder="https://…"
            type="url"
            autoFocus
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            helperText="Trang phải công khai, và bạn cần có quyền chỉnh sửa nội dung của nó."
          />
          <LabeledField
            label="Tên gọi (không bắt buộc)"
            placeholder="Suy từ địa chỉ nếu để trống"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />

          {add.isPending && (
            <Note>
              Đang đọc trang và chấm điểm lần đầu. Trang nhiều nội dung có thể mất vài chục giây.
            </Note>
          )}
          {add.isError && <Note tone="bad">{(add.error as Error).message}</Note>}

          <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
            Copilot chỉ đọc trang, không sửa gì trên đó. Mọi thay đổi do bạn tự áp dụng sau khi
            xem đề xuất.
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={close} disabled={add.isPending} color="inherit">
          Huỷ
        </Button>
        <Button
          variant="contained"
          onClick={() => add.mutate()}
          disabled={!canSubmit}
          startIcon={add.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {add.isPending ? 'Đang quét…' : 'Thêm và quét'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
